'use client'

import { ReactNode, useEffect, useId, useRef, useSyncExternalStore } from 'react'
import { createPortal } from 'react-dom'
import useBlockScroll from '@/app/hooks/useBlockScroll'
import useOnClickOutside from '@/app/hooks/useOnClickOutside'

interface LoanModalShellProps {
    badge?: { label: string; tone: 'success' | 'error' }
    leading?: ReactNode
    title: ReactNode
    subtitle?: ReactNode
    onClose?: () => void
    children: ReactNode
    maxWidthClassName?: string
    paddingClassName?: string
    /** `callback`: the RDV modals of the AE funnel mock (radius 24, beige round close button, plain grey overlay) */
    chrome?: 'default' | 'callback'
}

const CHROMES = {
    default: {
        overlay: 'bg-black/40 backdrop-blur-sm',
        panel: 'mx-4 rounded-2xl shadow-2xl',
        padding: 'p-8',
        close: 'right-4 top-4 h-8 w-8 text-pale/50 hover:bg-neutral hover:text-pale',
        header: 'mb-4',
        headerCloseSpace: 'pr-8',
        title: 'text-[22px] font-semibold',
    },
    callback: {
        overlay: 'bg-[#393939]/55',
        panel: 'mx-3 rounded-3xl shadow-[0_1px_2px_-1px_rgba(0,0,0,.1),0_1px_3px_0_rgba(0,0,0,.1),0_1px_4px_0_rgba(0,0,0,.25)]',
        padding: 'px-5 py-[26px] md:px-10 md:py-[34px]',
        close: 'right-[18px] top-[18px] h-9 w-9 bg-neutral text-pale hover:bg-[#EFE3DE]',
        header: 'mb-1 pr-10',
        headerCloseSpace: '',
        title: 'text-[22px] font-bold md:text-[24px]',
    },
}

// Le portail n'existe qu'après hydratation : un store constant l'exprime sans setState dans un effet
const subscribeToNothing = () => () => {}

const BADGE_TONES = {
    success: 'bg-green-100 text-green-800',
    error: 'bg-red-100 text-red-700',
}

const BADGE_DOTS = {
    success: 'bg-green-600',
    error: 'bg-red-500',
}

const FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

const focusablesIn = (node: HTMLElement) =>
    Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter((el) => el.offsetParent !== null)

export default function LoanModalShell({
    badge,
    leading,
    title,
    subtitle,
    onClose,
    children,
    maxWidthClassName = 'max-w-[800px]',
    paddingClassName,
    chrome = 'default',
}: LoanModalShellProps) {
    const look = CHROMES[chrome]
    const modalRef = useRef<HTMLDivElement>(null)
    const titleId = useId()
    const subtitleId = useId()
    const mounted = useSyncExternalStore(
        subscribeToNothing,
        () => true,
        () => false
    )

    useBlockScroll()
    useOnClickOutside(modalRef, () => onClose?.(), 'mousedown', '.react-datepicker')

    const onCloseRef = useRef(onClose)
    useEffect(() => {
        onCloseRef.current = onClose
    }, [onClose])

    // Le focus doit revenir au déclencheur : sinon il repart en haut de page à la fermeture
    useEffect(() => {
        const trigger = document.activeElement as HTMLElement | null
        return () => trigger?.focus?.()
    }, [])

    // Focus initial isolé de `onClose` : inline chez les appelants, il ramènerait au 1er champ à chaque render
    useEffect(() => {
        const node = modalRef.current
        if (!node) return

        const first = focusablesIn(node)[0]
        if (first) first.focus()
        else node.focus()
    }, [mounted])

    useEffect(() => {
        const node = modalRef.current
        if (!node) return

        const handleKeyDown = (event: KeyboardEvent) => {
            // An open datepicker closes first, and being in a portal it is looked up on the document
            if (event.key === 'Escape') {
                if (!document.querySelector('.react-datepicker')) onCloseRef.current?.()
                return
            }
            if (event.key !== 'Tab') return

            const items = focusablesIn(node)
            if (!items.length) return

            const start = items[0]
            const end = items[items.length - 1]
            const active = document.activeElement
            // `aria-modal` promet un focus captif : un clic sur le fond l'a sorti de la modale
            if (!node.contains(active)) {
                event.preventDefault()
                ;(event.shiftKey ? end : start).focus()
            } else if (event.shiftKey && active === start) {
                event.preventDefault()
                end.focus()
            } else if (!event.shiftKey && active === end) {
                event.preventDefault()
                start.focus()
            }
        }

        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [mounted])

    if (!mounted) return null

    return createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
            <div className={`fixed inset-0 ${look.overlay}`} />
            <div
                ref={modalRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                aria-describedby={subtitle ? subtitleId : undefined}
                tabIndex={-1}
                className={`relative z-10 max-h-[90vh] w-full overflow-y-auto bg-white ${look.panel} ${paddingClassName ?? look.padding} ${maxWidthClassName}`}
            >
                {onClose && (
                    <button
                        onClick={onClose}
                        aria-label="Fermer"
                        className={`absolute flex items-center justify-center rounded-full ${look.close}`}
                    >
                        <i className="fa-regular fa-times" />
                    </button>
                )}

                {badge && (
                    <span
                        className={`mb-4 inline-flex items-center gap-2 rounded-full px-3 py-1 text-[13px] font-semibold ${BADGE_TONES[badge.tone]}`}
                    >
                        <span className={`h-2 w-2 rounded-full ${BADGE_DOTS[badge.tone]}`} />
                        {badge.label}
                    </span>
                )}

                <div className={`flex items-start gap-4 ${look.header} ${onClose ? look.headerCloseSpace : ''}`}>
                    {leading}
                    <div className="min-w-0 flex-1">
                        <h3
                            id={titleId}
                            style={{ fontFamily: 'var(--font-recoleta)' }}
                            className={`block px-0 leading-[1.2] text-pale ${look.title}`}
                        >
                            {title}
                        </h3>
                        {subtitle && (
                            <p id={subtitleId} className="mt-1 text-sm text-pale/70">
                                {subtitle}
                            </p>
                        )}
                    </div>
                </div>

                {children}
            </div>
        </div>,
        document.body
    )
}
